import React, { useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { positionMenuByTrigger } from '../utils/menuPosition';
import { useDispatch, useSelector } from 'react-redux';
import { setAudioList, removeTrack, setAutoPlay } from '../redux/slices/musicPlayerSlice';
import { clearListContext } from '../redux/slices/listContextSlice';
import { setShowSavePlayerModal, triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import { useToast } from '../contexts/ToastContext';
import LikeButton from './LikeButton';
import AddToPlaylistModal from './AddToPlaylistModal';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as PlayIcon } from '../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../assets/icons/pause.svg';
import { setActiveSearch, setTrackQuery } from '../redux/slices/djpalSlice';
import { searchTrack } from '../redux/thunk/djpalThunk';
import { setActiveTab } from '../redux/slices/appSlice';
import { findJinkeQueueItem } from '../utils/jinkeQueueDom';
import { isNative } from '../utils/webOnly';
import './CustomPlaylistPanel.scss';
import CoverArt from './CoverArt';

const QueueItemMenu = ({ track, trackIndex, onClose, onAddToPlaylist }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const menuRef = useRef(null);
    const audioList = useSelector(state => state.musicPlayer.audioList);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                onClose();
            }
        };
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [onClose]);

    return (
        <div className="cpl-context-menu" ref={menuRef} onClick={e => e.stopPropagation()}>
            <div className="cpl-menu-item" onClick={(e) => {
                e.stopPropagation();
                onClose();
                onAddToPlaylist(track);
            }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="5" x2="21" y2="5" />
                    <line x1="3" y1="11" x2="21" y2="11" />
                    <line x1="3" y1="17" x2="12" y2="17" />
                    <line x1="18" y1="14" x2="18" y2="22" />
                    <line x1="14" y1="18" x2="22" y2="18" />
                </svg>
                Add to Playlist
            </div>
            <div className="cpl-menu-item" onClick={(e) => {
                e.stopPropagation();
                const text = track.singer ? `${track.singer} - ${track.name}` : track.name;
                navigator.clipboard.writeText(text);
                toast.success('Copied');
                onClose();
            }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                </svg>
                Copy Name
            </div>
            <div className="cpl-menu-item cpl-menu-danger" onClick={(e) => {
                e.stopPropagation();
                // Route the delete through jinke's own delete button so ITS
                // internal list updates too (jinke's non-clear update path is
                // append-only — a Redux-only remove leaves a ghost entry that
                // still plays). onAudioListsChange then mirrors Redux.
                const item = findJinkeQueueItem(audioList, trackIndex);
                const del = item?.querySelector('.player-delete');
                if (del) del.click();
                else dispatch(removeTrack(trackIndex));
                toast.info('Removed from queue');
                onClose();
            }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                Remove from Queue
            </div>
        </div>
    );
};

const CustomPlaylistPanel = ({ visible, onClose, currentTrackInfo, autoPlay }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { audioList } = useSelector(state => state.musicPlayer);
    const { selectedPlaylistId, selectedPlaylistName } = useSelector(state => state.playlist);
    const [openMenuIdx, setOpenMenuIdx] = useState(null);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
    const [playlistModalTrack, setPlaylistModalTrack] = useState(null);
    const menuWrapRef = useRef(null);
    const menuBtnRef = useRef(null); // the kebab button that opened the current menu

    const currentSrc = currentTrackInfo?.musicSrc;

    const handlePlayTrack = useCallback((idx) => {
        const track = audioList[idx];
        if (!track) return;

        // Native: jinke's element/DOM drives nothing — pause via the hijacked
        // play/pause button, jump via the URL-addressed plugin route (see
        // MusicPlayer's mixgoat-native-play-url listener; a same-URL ask
        // resumes instead of restarting).
        if (isNative) {
            if (currentSrc && track.musicSrc === currentSrc && autoPlay) {
                document.querySelector('.react-jinke-music-player-main .player-content .play-btn')?.click();
                return;
            }
            window.dispatchEvent(new CustomEvent('mixgoat-native-play-url', { detail: { url: track.musicSrc } }));
            dispatch(setAutoPlay(true));
            return;
        }

        // If clicking the currently playing track, toggle pause/play
        if (currentSrc && track.musicSrc === currentSrc) {
            if (window.musicPlayerInstance) {
                if (autoPlay) {
                    window.musicPlayerInstance.pause();
                } else {
                    window.musicPlayerInstance.play?.();
                }
            }
            return;
        }

        // Click the library's hidden audio-item — matched by CONTENT, not
        // index (index clicks hit the wrong track if the lists ever diverge).
        const item = findJinkeQueueItem(audioList, idx);
        if (item) item.click();
    }, [audioList, currentSrc, autoPlay, dispatch]);

    const handleClearAll = useCallback(() => {
        // Clearing the queue clears the ambient list too — otherwise the
        // next single-track play resumes a list the user just dismissed.
        dispatch(clearListContext());
        dispatch(setAudioList([]));
        toast.info('Queue cleared');
    }, [dispatch, toast]);

    const handleSearchTrack = useCallback((e, track) => {
        e.stopPropagation();
        const query = track.singer ? `${track.singer} - ${track.name}` : track.name;
        // Sub-tab + query BEFORE setActiveTab so the nav marker is typed and the
        // result replaces it (one Back = back here). setActiveTab sets the
        // category itself — a separate setActiveCategory pushes a stray
        // landing-page entry into the back stack.
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(query));
        dispatch(setActiveTab('djpal'));
        setTimeout(() => dispatch(searchTrack({ trackName: query, trackId: track.trackId || null, variant: 'A', artworkUrl: track.cover || track.artwork || null })), 150);
    }, [dispatch]);

    const handleSave = useCallback(async () => {
        if (selectedPlaylistId && selectedPlaylistName) {
            try {
                const filePaths = audioList.map(item => {
                    try {
                        const url = new URL(item.musicSrc);
                        const pathParam = url.searchParams.get('path');
                        return pathParam ? decodeURIComponent(pathParam) : item.musicSrc;
                    } catch (error) {
                        return item.musicSrc;
                    }
                }).filter(Boolean);

                await window.electron.db.playlists.clear(selectedPlaylistId);
                await window.electron.db.playlists.addTracks(selectedPlaylistId, filePaths);
                dispatch(triggerPlaylistRefresh());
                toast.success(`Saved to "${selectedPlaylistName}"`);
            } catch (error) {
                toast.error('Failed to save');
            }
        } else {
            dispatch(setShowSavePlayerModal(true));
        }
    }, [dispatch, audioList, selectedPlaylistId, selectedPlaylistName, toast]);

    const handleOpenMenu = useCallback((e, idx) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        menuBtnRef.current = e.currentTarget;
        setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        setOpenMenuIdx(prev => prev === idx ? null : idx);
    }, []);

    // Once the menu renders, flip it above the kebab when it would spill past
    // the viewport bottom (rows near the bottom of the queue panel).
    useLayoutEffect(() => {
        if (openMenuIdx === null || !menuWrapRef.current || !menuBtnRef.current) return;
        const rect = menuBtnRef.current.getBoundingClientRect();
        const { top } = positionMenuByTrigger(menuWrapRef.current, rect, { align: null });
        setMenuPos(prev => (prev.top === top ? prev : { ...prev, top }));
    }, [openMenuIdx]);

    const handleAddToPlaylist = useCallback((track) => {
        setPlaylistModalTrack({ artist: track.singer, title: track.name, artwork: track.cover });
    }, []);

    // Mobile close animation: keep the panel mounted for the slide-down after
    // `visible` flips false — driven off the prop transition so EVERY close
    // path animates (header ✕, backdrop tap, the player's queue toggle).
    // Desktop unmounts immediately, exactly as before.
    const [rendered, setRendered] = useState(visible);
    const [closing, setClosing] = useState(false);
    const closeTimerRef = useRef(null);
    useEffect(() => {
        if (visible) {
            clearTimeout(closeTimerRef.current);
            setClosing(false);
            setRendered(true);
            return undefined;
        }
        if (!rendered) return undefined;
        const isMobile = typeof window !== 'undefined'
            && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
        if (!isMobile) { setRendered(false); return undefined; }
        setClosing(true);
        closeTimerRef.current = setTimeout(() => {
            setClosing(false);
            setRendered(false);
        }, 240);
        return () => clearTimeout(closeTimerRef.current);
    }, [visible, rendered]);

    if (!rendered) return null;

    return ReactDOM.createPortal(
        <>
            {/* Mobile-only (display:none on desktop): swallows the tap that
                used to fall through to the row behind the panel — tapping the
                background now closes the queue instead of playing whatever
                happened to be under the finger. */}
            <div className={`cpl-backdrop${closing ? ' cpl-closing' : ''}`} onClick={onClose} />
            <div className={`custom-playlist-panel${closing ? ' cpl-closing' : ''}`}>
                <div className="cpl-header">
                    <div className="cpl-header-left">
                        <span className="cpl-title">
                            {selectedPlaylistName || 'Queue'}
                        </span>
                        <span className="cpl-count">{audioList.length} track{audioList.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="cpl-header-actions">
                        <button className="cpl-header-btn" title="Save playlist" onClick={handleSave}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                <polyline points="17 21 17 13 7 13 7 21"/>
                                <polyline points="7 3 7 8 15 8"/>
                            </svg>
                        </button>
                        <button className="cpl-header-btn" title="Clear all" onClick={handleClearAll}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                        </button>
                        <button className="cpl-header-btn cpl-close-btn" title="Close" onClick={onClose}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="cpl-content">
                    {audioList.length === 0 ? (
                        <div className="cpl-empty">No tracks in queue</div>
                    ) : (
                        <ul className="cpl-list">
                            {audioList.map((track, idx) => {
                                const firstMatchIdx = audioList.findIndex(t => t.musicSrc === currentSrc);
                                const isPlaying = currentSrc && track.musicSrc === currentSrc && idx === firstMatchIdx && autoPlay;
                                const displayText = track.singer
                                    ? `${track.singer} - ${track.name}`
                                    : track.name;
                                return (
                                    <li
                                        key={`${track.name}-${idx}`}
                                        className={`cpl-item ${isPlaying ? 'playing' : ''}`}
                                        onClick={() => handlePlayTrack(idx)}
                                    >
                                        <div className="cpl-art">
                                            {track.cover ? (
                                                <CoverArt src={track.cover} size={40} />
                                            ) : (
                                                <div className="cpl-art-placeholder">&#9835;</div>
                                            )}
                                            <div className="cpl-play-overlay">
                                                {isPlaying ? (
                                                    // Now-playing: animated equalizer bars (same signal as the Feed).
                                                    // Clicking the item still toggles pause.
                                                    <div className="cpl-eq" aria-label="Playing">
                                                        <span></span><span></span><span></span><span></span>
                                                    </div>
                                                ) : <PlayIcon />}
                                            </div>
                                        </div>
                                        <span className="cpl-track-name" title={displayText}>
                                            {displayText}
                                        </span>
                                        <div className="cpl-actions" onClick={e => e.stopPropagation()}>
                                            <button className="cpl-action-btn" title="Search in Mixpedia" onClick={(e) => handleSearchTrack(e, track)}>
                                                <SearchIcon />
                                            </button>
                                            <LikeButton track={{ artist: track.singer, title: track.name, artwork: track.cover }} />
                                            <button className="cpl-action-btn cpl-more-btn" title="More" onClick={(e) => handleOpenMenu(e, idx)}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                    <circle cx="5" cy="12" r="2.5" />
                                                    <circle cx="12" cy="12" r="2.5" />
                                                    <circle cx="19" cy="12" r="2.5" />
                                                </svg>
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
            {openMenuIdx !== null && audioList[openMenuIdx] && (
                <div ref={menuWrapRef} style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 10001 }}>
                    <QueueItemMenu
                        track={audioList[openMenuIdx]}
                        trackIndex={openMenuIdx}
                        onClose={() => setOpenMenuIdx(null)}
                        onAddToPlaylist={handleAddToPlaylist}
                    />
                </div>
            )}
            {playlistModalTrack && (
                <AddToPlaylistModal
                    show={true}
                    onHide={() => setPlaylistModalTrack(null)}
                    track={playlistModalTrack}
                />
            )}
        </>,
        document.body
    );
};

export default CustomPlaylistPanel;
